import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { Edit, Check, Close, SyncProblemOutlined } from '@mui/icons-material';
import {
  useRequestVerificationMutation,
  useTriggerFinanceEventMutation,
  useUpdateEventAmendmentMutation,
  useUpdateEventPayoutAmendmentMutation,
} from '../../store/slices/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { openMessage } from '../../store/slices/showMessageSlice';
import { pending } from '../../constants/bankAccountStatus';
import CustomButton from '../../components/@extended/CustomButton';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import ExecuteEventDialog from './ExecuteEventDialog';

const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;
  const data = error?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  if (error?.message) return error.message;
  return fallback;
};

const EventsSection = ({
  protocolNumber,
  siteEntityId,
  events,
  subjects = [],
  onClose,
  onRefresh,
  isEditable = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const actor = useSelector((state) => state?.userDetails?.loggedInUser?.email);

  const [editableRowId, setEditableRowId] = useState(null);
  const [editData, setEditData] = useState({});
  const [editingSubjectPayment, setEditingSubjectPayment] = useState(null);
  const [subjectPaymentValues, setSubjectPaymentValues] = useState({});
  const [executingEventId, setExecutingEventId] = useState(null);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [subjectDialogEvent, setSubjectDialogEvent] = useState(null);

  const [updateEventPayoutAmendment, { isLoading }] = useUpdateEventPayoutAmendmentMutation();
  const [requestVerification, { isLoading: verificationLoading }] = useRequestVerificationMutation();
  const [updateEventAmendment, { isLoading: isAmendmentLoading }] = useUpdateEventAmendmentMutation();
  const [triggerFinanceEvent, { isLoading: isTriggerLoading }] = useTriggerFinanceEventMutation();

  const isEditingInProgress = editableRowId !== null || editingSubjectPayment !== null;
  const siteSubjectCount = useMemo(
    () => subjects.filter((entry) => entry?.subjectEntity?.email).length,
    [subjects]
  );

  const handleEdit = (payout) => {
    setEditableRowId(payout.id);
    setEditData({
      amount: payout.amount,
    });
  };

  const handleSave = async (payout, event) => {
    const result = await updateEventPayoutAmendment({
      protocolNumber,
      siteEntityId,
      eventId: event.id,
      payoutId: payout.id,
      body: {
        amount: editData.amount,
      },
    });
    if (result.data) {
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: result.data.message || 'Payout updated successfully!',
        })
      );
    } else {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: result.error.data.message || 'Failed to update payout!',
        })
      );
    }
    onClose();
    setEditableRowId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditableRowId(null);
    setEditData({});
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditSubjectPayment = (eventId, amount, currency) => {
    setEditingSubjectPayment(eventId);
    setSubjectPaymentValues({
      amount,
      currency,
    });
  };

  const handleSaveSubjectPayment = async (eventId) => {
    const result = await updateEventAmendment({
      protocolNumber,
      siteEntityId,
      eventId: eventId,
      body: {
        subjectPaymentAmount: Number(subjectPaymentValues.amount),
      },
    });
    if (result.data) {
      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: result.data.message || 'Subject payment updated successfully!',
        })
      );
    } else {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: result.error.data.message || 'Failed to update subject payment!',
        })
      );
    }

    onClose();
    setEditingSubjectPayment(null);
    setSubjectPaymentValues({});
  };

  const handleRequestVerification = async (entity) => {
    try {
      const res = await requestVerification(entity?.id).unwrap();
      if (res?.success) {
        dispatch(
          openMessage({
            messageSeverity: 'success',
            message: `Requested verification for ${entity?.email}`,
          })
        );
      }
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to request verification for ${entity?.email}`,
        })
      );
    }
  };

  const handleCancelSubjectPayment = () => {
    setEditingSubjectPayment(null);
    setSubjectPaymentValues({});
  };

  const getRecipientName = (payout) => {
    return payout.targetEntity.addditionalInfo ? JSON.parse(payout.targetEntity.addditionalInfo).name : 'N/A';
  };

  const runTriggerForEvent = async (event, subjectEmails = []) => {
    setExecutingEventId(event.id);

    const triggers =
      event.isPayableToSubject && subjectEmails.length > 0
        ? subjectEmails.map((subjectEmail) => ({ subjectEmail }))
        : [{ subjectEmail: undefined }];

    let successCount = 0;
    let failureCount = 0;
    const errors = [];

    try {
      for (const trigger of triggers) {
        try {
          await triggerFinanceEvent({
            protocolNumber,
            siteEntityId,
            eventKey: event.eventKey,
            subjectEmail: trigger.subjectEmail,
            actor,
          }).unwrap();
          successCount += 1;
        } catch (error) {
          failureCount += 1;
          errors.push(getApiErrorMessage(error));
        }
      }

      if (failureCount === 0) {
        dispatch(
          openMessage({
            messageSeverity: 'success',
            message:
              triggers.length > 1
                ? `Payments initiated for ${successCount} subject(s).`
                : resultMessageForEvent(event),
          })
        );
      } else if (successCount > 0) {
        dispatch(
          openMessage({
            messageSeverity: 'warning',
            message: `${successCount} payment(s) initiated, ${failureCount} failed. ${errors[0] || ''}`,
          })
        );
      } else {
        dispatch(
          openMessage({
            messageSeverity: 'error',
            message: errors[0] || 'Failed to execute transaction.',
          })
        );
      }
    } finally {
      setExecutingEventId(null);
      setConfirmEvent(null);
      setSubjectDialogEvent(null);
    }
  };

  const resultMessageForEvent = (event) => {
    if (event.approvalNeeded) {
      return 'Transaction recorded as pending approval.';
    }
    return 'Transaction executed successfully.';
  };

  const handleExecuteClick = (event) => {
    if (event.isPayableToSubject) {
      setSubjectDialogEvent(event);
      return;
    }
    setConfirmEvent(event);
  };

  const canExecuteEvent = (event) => {
    if (!isEditable || isEditingInProgress || isTriggerLoading) return false;
    if (event.isPayableToSubject) {
      return siteSubjectCount > 0;
    }
    return (event.eventPayoutDetails?.length ?? 0) > 0;
  };

  const getExecuteDisabledReason = (event) => {
    if (event.isPayableToSubject && siteSubjectCount === 0) {
      return 'No subjects linked to this site.';
    }
    if (!event.isPayableToSubject && (event.eventPayoutDetails?.length ?? 0) === 0) {
      return 'No payout recipients configured for this event.';
    }
    return '';
  };

  return (
    <Box sx={{ width: '100%' }}>
      {events.map((event) => {
        const isExecuting = executingEventId === event.id;
        const disabledReason = getExecuteDisabledReason(event);

        return (
          <Box key={event.id} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="subtitle1">{event.eventName}</Typography>
              {isEditable && (
                <Tooltip title={disabledReason || 'Execute payouts for this event'}>
                  <span>
                    <CustomButton
                      variant="contained"
                      size="small"
                      onClick={() => handleExecuteClick(event)}
                      disabled={!canExecuteEvent(event) || isExecuting}
                    >
                      {isExecuting ? 'Executing...' : 'Execute'}
                    </CustomButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>Event Key:</strong> {event.eventKey}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {event.type}
              </Typography>
              <Typography variant="body2">
                <strong>Approval Needed:</strong> {event.approvalNeeded ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Payable to Subject:</strong> {event.isPayableToSubject ? 'Yes' : 'No'}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  '&:hover .edit-icon': {
                    opacity: 1,
                  },
                }}
              >
                {editingSubjectPayment === event.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Subject Payment:</strong>
                    </Typography>
                    <TextField
                      size="small"
                      value={subjectPaymentValues.amount}
                      onChange={(e) => setSubjectPaymentValues((prev) => ({ ...prev, amount: e.target.value }))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      sx={{ width: '100px' }}
                    />
                    {isAmendmentLoading ? (
                      <CircularProgress size={15} />
                    ) : (
                      <>
                        <IconButton size="small" color="success" onClick={() => handleSaveSubjectPayment(event.id)}>
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={handleCancelSubjectPayment}>
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2">
                      <strong>Subject Payment:</strong> {event.subjectPaymentAmount}{' '}
                        {event.subjectPaymentCurrency?.toUpperCase()}
                      </Typography>
                      {isEditable && (
                        <IconButton
                          className="edit-icon"
                          size="small"
                          sx={{
                            ml: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                          }}
                          onClick={() =>
                            handleEditSubjectPayment(event.id, event.subjectPaymentAmount, event.subjectPaymentCurrency)
                          }
                        >
                          <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {isEditable && disabledReason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {disabledReason}
              </Typography>
            )}

            {event.eventPayoutDetails?.length > 0 && (
              <TableContainer>
                <Table size="medium">
                  <CustomTableHead>
                    <TableRow>
                      <CustomTableHeadCell>
                        <strong>Recipient</strong>
                      </CustomTableHeadCell>
                      <CustomTableHeadCell>
                        <strong>Email</strong>
                      </CustomTableHeadCell>
                      <CustomTableHeadCell>
                        <strong>Account Status</strong>
                      </CustomTableHeadCell>
                      <CustomTableHeadCell>
                        <strong>Amount</strong>
                      </CustomTableHeadCell>
                      <CustomTableHeadCell>
                        <strong>Currency</strong>
                      </CustomTableHeadCell>
                      {isEditable && <CustomTableHeadCell></CustomTableHeadCell>}
                    </TableRow>
                  </CustomTableHead>
                  <TableBody>
                    {event.eventPayoutDetails.map((payout) => (
                      <CustomTableRow key={payout.id}>
                        <TableCell>
                          <Tooltip title={getRecipientName(payout)}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '150px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {getRecipientName(payout)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={payout.targetEntity.email}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '180px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {payout.targetEntity.email}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            color: pending.includes(payout.targetEntity?.accountStatus)
                              ? theme.palette.warning.main
                              : theme.palette.success.main,
                            fontWeight: '700',
                          }}
                        >
                          {payout.targetEntity.accountStatus?.[0]?.toUpperCase() +
                            payout.targetEntity.accountStatus?.slice(1)}
                          {pending.includes(payout.targetEntity?.accountStatus) && (
                            <Tooltip
                              title={
                                <Box sx={{ p: 1 }}>
                                  <Typography variant="body2">
                                    Onboarding/KYC pending: Request verification
                                  </Typography>
                                </Box>
                              }
                              arrow
                            >
                              <IconButton
                                aria-label="request-verification"
                                onClick={() => handleRequestVerification(payout?.targetEntity)}
                                disabled={verificationLoading}
                                sx={{
                                  color: (theme) => theme.palette.warning.main,
                                }}
                              >
                                <SyncProblemOutlined />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {editableRowId === payout.id ? (
                            <TextField
                              size="small"
                              value={editData.amount}
                              sx={{ width: 120 }}
                              onChange={(e) => handleInputChange('amount', e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          ) : (
                            `$ ${payout.amount}`
                          )}
                        </TableCell>
                        <TableCell>{payout.currency.toUpperCase()}</TableCell>
                        {isEditable && (
                          <TableCell align="center">
                            {editableRowId === payout.id ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                {isLoading ? (
                                  <CircularProgress size={15} />
                                ) : (
                                  <>
                                    <IconButton color="success" onClick={() => handleSave(payout, event)} size="small">
                                      <Check fontSize="small" />
                                    </IconButton>
                                    <IconButton color="error" onClick={handleCancel} size="small">
                                      <Close fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(payout)}
                                disabled={isLoading || isAmendmentLoading || editableRowId !== null}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </CustomTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ pt: 2 }}>
              <Divider />
            </Box>
          </Box>
        );
      })}

      <ConfirmationDialog
        open={Boolean(confirmEvent)}
        title="Execute Transaction"
        message={
          confirmEvent
            ? `Execute payouts for "${confirmEvent.eventName}" (${confirmEvent.eventKey})? This will process all configured payout recipients for this event.`
            : ''
        }
        buttonLabel="Execute"
        handleClose={() => setConfirmEvent(null)}
        handleConfirm={() => runTriggerForEvent(confirmEvent)}
      />

      <ExecuteEventDialog
        open={Boolean(subjectDialogEvent)}
        event={subjectDialogEvent}
        subjects={subjects}
        loading={isTriggerLoading}
        onClose={() => setSubjectDialogEvent(null)}
        onConfirm={(selectedEmails) => runTriggerForEvent(subjectDialogEvent, selectedEmails)}
      />
    </Box>
  );
};

export default EventsSection;
